import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { get, responseValidator } from "../api";

type Props<T> = {
    ref: React.RefObject<HTMLElement>;
    url: string;
    initialList?: T[];
    open?: boolean;
};

export default function useLazyLoad<T>({ ref, url, initialList, open }: Props<T>) {
    const [loading, setLoading] = useState(false);
    const [list, setList] = useState<T[] | undefined>(initialList);
    const [nextUrl, setNextUrl] = useState<string | null>(url);
    const observerRef = useRef<IntersectionObserver | null>(null);

    const reset = useCallback(
        (data?: T[]) => {
            setNextUrl(url);
            setLoading(false);
            setList(data ? data : initialList);
        },
        [url]
    );

    useEffect(() => {
        let ignore = false;

        const fetchData = async (fetchUrl: string) => {
            setLoading(true);
            await get((data, status) => {
                setLoading(false);
                if (responseValidator(status) && !ignore) {
                    setNextUrl(data.next || null);
                    setList((prevList) => (prevList ? [...prevList, ...data.results] : data.results));
                }
            }, fetchUrl);
        };

        if (!list) (async () => await fetchData(url))();
        else if (nextUrl && ref.current) {
            observerRef.current = new IntersectionObserver(
                async (entries) => {
                    const [entry] = entries;
                    if (entry.isIntersecting && !loading) {
                        observerRef.current?.disconnect();
                        await fetchData(nextUrl);
                    }
                },
                { threshold: 0.5 }
            );

            if (open === undefined || open) observerRef.current.observe(ref.current);
        }

        return () => {
            observerRef.current?.disconnect();
            ignore = true;
        };
    }, [list, nextUrl, ref, url, open]);

    return useMemo(() => ({ loading, list, setList, reset }), [loading, list, setList, reset]);
}
